import { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { CentralChatPanelBody } from './CentralChatPanelBody';
import { useCentralChatRuntime } from './centralChatRuntime';
import { useNarrowScreen, useViewportWidth } from './useNarrowScreen';
import { useRailResize } from './useRailResize';

/**
 * The side panel the header badger opens. It is Carl's rail body in a column that slides in
 * from the right and PUSHES the page (an in-flow sibling of the page column, never a fixed
 * overlay), with the rail's resize and stored width. Closed it renders nothing at all: the
 * badger in the top bar is the opener, so there is no collapsed strip. Under `md` it is the
 * full-screen modal window, as the bubble's window is.
 *
 * Open state is the runtime's `bubbleOpen` (persisted per tenant and user, so the audit
 * drawer's "Chat with AI" opens it too). Close: the Close chat button, Escape, or the badger.
 */
const MAX_VIEWPORT_SHARE = 0.4;
const FOCUSABLE = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

function focusBadger() {
  document.querySelector<HTMLElement>('[data-testid="header-badger"]')?.focus();
}

export function CentralChatHeaderPanel() {
  const chat = useCentralChatRuntime();
  // The panel never takes more than 40% of the window, whatever width was stored.
  const resize = useRailResize(Math.floor(useViewportWidth() * MAX_VIEWPORT_SHARE));
  const narrow = useNarrowScreen();
  const open = chat.bubbleOpen;
  const sectionRef = useRef<HTMLElement>(null);

  // Closing from inside the panel (button, Escape) would drop focus to the page: hand it to the badger.
  const closeBubble = () => {
    const inside = sectionRef.current?.contains(document.activeElement) ?? false;
    chat.closeBubble();
    if (inside) focusBadger();
  };
  const closeRef = useRef(closeBubble);
  useEffect(() => {
    closeRef.current = closeBubble;
  });

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeRef.current();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  // The full-screen window is modal: keep the page behind it from scrolling while it is
  // open, and put the old value back when it closes.
  useEffect(() => {
    if (!open || !narrow) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open, narrow]);

  // The full-screen window is modal: on open move focus to its close button, keep Tab inside
  // while it is open, and give focus back to where it was (the badger) when it closes.
  useEffect(() => {
    if (!open || !narrow) return undefined;
    const previous = document.activeElement as HTMLElement | null;
    const section = sectionRef.current;
    section?.querySelector<HTMLElement>('button[aria-label="Close chat"]')?.focus();
    const trap = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || !section) return;
      const items = Array.from(section.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || !section.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !section.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', trap);
    return () => {
      document.removeEventListener('keydown', trap);
      if (previous?.isConnected) previous.focus();
      else focusBadger();
    };
  }, [open, narrow]);

  if (!open) return null;

  const closeButton = (
    <button
      type="button"
      onClick={closeBubble}
      title="Close chat"
      aria-label="Close chat"
      className="grid size-8 place-items-center rounded-md text-ink-2 transition-colors hover:bg-paper-2 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral/40"
    >
      <X className="size-4" />
    </button>
  );

  if (narrow) {
    return (
      <section
        ref={sectionRef}
        aria-label="Emjae chat"
        role="dialog"
        aria-modal="true"
        className="fixed inset-0 z-50 flex flex-col overflow-hidden overscroll-contain bg-warm-bg pb-[env(safe-area-inset-bottom)] pt-[env(safe-area-inset-top)] text-ink"
      >
        <CentralChatPanelBody chromeActions={closeButton} showStatusBanner onLeave={chat.closeBubble} />
      </section>
    );
  }

  return (
    <div
      data-testid="chat-header-panel"
      className="shrink-0 animate-in slide-in-from-right duration-200 motion-reduce:animate-none"
      style={{ width: resize.width }}
    >
      <div className="sticky top-0 h-svh">
        <section
          ref={sectionRef}
          aria-label="Emjae chat"
          className="relative flex h-full min-h-0 flex-col border-l border-warm-border bg-warm-bg text-ink"
        >
          <div
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize chat panel"
            aria-valuenow={resize.width}
            aria-valuemin={resize.min}
            aria-valuemax={resize.max}
            tabIndex={0}
            onMouseDown={(e) => {
              e.preventDefault();
              resize.startDrag();
            }}
            onKeyDown={resize.onSeparatorKeyDown}
            className="group/resize absolute bottom-0 left-0 top-0 z-10 flex w-1.5 cursor-col-resize items-center focus-visible:outline-none"
          >
            <div className="absolute inset-y-0 left-0 w-1.5 bg-transparent transition-colors group-hover/resize:bg-coral/20 group-focus-visible/resize:bg-ring/40" />
          </div>
          <CentralChatPanelBody chromeActions={closeButton} showStatusBanner />
        </section>
      </div>
    </div>
  );
}
